import Auth from "../../../Auth.js";
import { ENTITY_IMAGES_PAGE_SIZE } from "../../../../../src/shared/entityImages";
import type { EntityImageListItem, ReferenceImagePayload, StylePresetOption } from "./types";

export interface SaveImageResponse {
  success: true;
  imageId: string;
  imageUrl: string;
  /** Absent from older servers. */
  isCover?: boolean;
}

export interface SaveGeneratedImagePayload {
  invocationId: string;
  mimeType: string;
  base64: string;
}

export interface ListEntityImagesResponse {
  success: true;
  cover: EntityImageListItem | null;
  images: EntityImageListItem[];
  pagination: { hasMore: boolean; nextCursor: string | null; totalAlternateCount: number };
}

export interface StylePresetsResponse<TPreset extends StylePresetOption = StylePresetOption> {
  success: true;
  stylePresets: TPreset[];
}

export interface GenerateCandidateResponse {
  success: true;
  invocationId: string;
  // null if the save itself failed — the image is still billed either way (see src/services/entityImageGeneration.ts).
  imageId: string | null;
  imageUrl: string | null;
  isCover?: boolean;
  candidate: {
    stylePresetId: string;
    stylePresetLabel: string;
    ok: true;
    mimeType: string;
    // Present only as a fallback when the save (and so imageUrl) failed.
    base64?: string;
    model: string;
    prompt: string;
  };
}

export interface DeleteImageResponse {
  success: true;
  wasActive: boolean;
}

export interface SetCoverImageResponse {
  success: true;
  imageId: string;
  imageUrl: string;
}

/** The entity-agnostic image endpoints, all served under `${basePath}/:id/images`. */
export function createEntityImageApi(basePath: "/api/characters" | "/api/worldbuildingEntries") {
  const imagesUrl = (entityId: string, suffix = "") => `${basePath}/${entityId}/images${suffix}`;

  return {
    imagesUrl,
    listStylePresets<TResponse = StylePresetsResponse>(entityId: string): Promise<TResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, "/style-presets"), {});
    },
    saveGenerated(entityId: string, payload: SaveGeneratedImagePayload): Promise<SaveImageResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, "/save"), { invocationId: payload.invocationId, mimeType: payload.mimeType, base64: payload.base64 });
    },
    upload(entityId: string, payload: ReferenceImagePayload): Promise<SaveImageResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, "/upload"), { mimeType: payload.mimeType, base64: payload.base64 });
    },
    list(entityId: string, params: { limit: number; cursor?: string | null } = { limit: ENTITY_IMAGES_PAGE_SIZE }): Promise<ListEntityImagesResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, "/list"), { limit: params.limit, cursor: params.cursor ?? undefined });
    },
    remove(entityId: string, imageId: string): Promise<DeleteImageResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, `/${imageId}`), { method: "DELETE" });
    },
    setCover(entityId: string, imageId: string): Promise<SetCoverImageResponse> {
      return Auth.fetchWithIdToken(imagesUrl(entityId, `/${imageId}/cover`), {});
    },
  };
}
